import { ReactElement, useState } from "react";
import { useSimplePurchase } from "../../../rounds/usePurchase";
import WinInfoRound from "../wininforound";
import { useTranslation } from "react-i18next";
import { getSuggestion } from "../../../common/proPickUtil";
import { operatorAdviceBetSuccess } from "../../../App";
import CouponHeaderSlim from "./components/couponheader";
import ShowResultOfBet from "./components/showresultofbet";
import { ErrorResponse } from "./components/confirmpayment";
import { IsRightToLeftTheme } from "../../../theme/themehelper";
import SlimCoupon from "../../../slimcoupon/slimcoupon";
import { Alert } from "@mui/material";
import { getJackpot } from "../../../common/roundhelpers";
import { DetailedRound } from "../../../models/round";
import { System } from "../../../models/couponshares";
import { trackPurchase } from "../../../analytics/analytics";

interface ProPickSuggestion {
  dif: string | null;
  numFull: number;
  numHalf: number;
}

interface CouponFormProps {
  round: DetailedRound;
  onChangeFixture: (params: number) => void;
  setShowRoundInfo: (show: boolean) => void;
  showRoundInfo: boolean;
  proPickSuggestion: ProPickSuggestion;
  setShowProPick: (show: boolean) => void;
  showProPick: boolean;
  setProPickRows: (rows: any) => void;
  proPickRows: any;
  setChosenRSystem: (system: System) => void;
  chosenRSystem: System;
  showRSystem: boolean;
  setShowRSystem: (show: boolean) => void;
}

function CouponForm({
  round,
  onChangeFixture,
  setShowRoundInfo,
  showRoundInfo,
  proPickSuggestion,
  setShowProPick,
  showProPick,
  setProPickRows,
  proPickRows,
  setChosenRSystem,
  chosenRSystem,
  showRSystem,
  setShowRSystem,
}: CouponFormProps): ReactElement {
  const [stakeMultiplier, setStakeMultiplier] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResultOfBet, setShowResultOfBet] = useState(false);
  const purchase = useSimplePurchase(round, stakeMultiplier);
  const [incomingRowsCalculated, setIncomingRowsCalculated] = useState(false);
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitError, setSubmitError] = useState<ErrorResponse | undefined>(undefined);

  const handleErrorMessageClose = (): void => {
    if (submitFailed) {
      setSubmitFailed(false);
      setSubmitError(undefined);
      setShowConfirm(false);
    }
  };

  if (
    proPickSuggestion &&
    !incomingRowsCalculated &&
    (proPickSuggestion.numFull > 0 || proPickSuggestion.numHalf > 0)
  ) {
    setIncomingRowsCalculated(true);
    getSuggestion(proPickSuggestion, round.fixtures, setProPickRows);
  }

  const isRightToLeft = IsRightToLeftTheme(window.theme);

  const onSubmit = async (coupon: any): Promise<boolean> => {
    setSubmitting(true);
    const res = await purchase(coupon) as Response;
    const data = await res.json();
    setSubmitting(false);
    setProPickRows(undefined);

    if (res.status === 200) {
      setSubmitFailed(false);
      setSubmitError(undefined);

      if (data && data.transactionId) {
        const betSuccessEvent = {
          type: "bet_success",
          amount: data.amount,
          currency: data.currency,
          transactionId: data.transactionId,
          roundId: data.roundId,
        };
        operatorAdviceBetSuccess(betSuccessEvent);
        setShowConfirm(false);
        setShowResultOfBet(true);
        trackPurchase(data, "coupon", coupon.bets[0].system.type, stakeMultiplier);
      }
      return true;
    } else {
      setSubmitError(data);
      setSubmitFailed(true);
      return false;
    }
  };

  const { openTo } = round || {};

  const turnoverAmount: number | string = round.operatorTurnover
    ? round.operatorTurnover.amount
    : "NA";
  const turnoverCurrency: string = round.operatorTurnover
    ? round.operatorTurnover.currency
    : "NA";
  const startDate = new Date(openTo);

  const jackpot = getJackpot(turnoverCurrency, round);

  const isAfterDeadline = startDate < new Date(Date.now());

  function setSlimProPick(value: boolean): void {
    setShowProPick(value);
  }

  function setSlimShowSystem(value: boolean): void {
    setShowRSystem(value);
  }

  let reducedFull: number | string = 0;
  let reducedHalf: number | string = 0;

  if (chosenRSystem.type === "REDUCED" && chosenRSystem.reducedSystemId) {
    const parts = chosenRSystem.reducedSystemId.split("_");
    if (parts !== undefined && parts.length > 2) {
      reducedFull = parts[1];
      reducedHalf = parts[2];
    }
  }

  return (
    <>
      {showResultOfBet && (
        <ShowResultOfBet toggle={setShowResultOfBet} onClose={() => setShowResultOfBet(false)} />
      )}

      <>
        <div>
          <CouponHeaderSlim
            jackpot={jackpot ?? 0}
            round={round}
            t={t}
            turnoverAmount={turnoverAmount}
            turnoverCurrency={turnoverCurrency}
            setShowRoundInfo={setShowRoundInfo}
            setShowProPick={setSlimProPick}
            setShowSystem={setSlimShowSystem}
            showRoundInfo={showRoundInfo}
            showProPick={showProPick}
            showSystem={showRSystem}
          />
        </div>

        {chosenRSystem.type === "REDUCED" && (
          <Alert severity="info">
            {t("fill_coupon") +
              " " +
              reducedFull +
              " " +
              t("full_cover").toLowerCase() +
              " " +
              t("and") +
              " " +
              reducedHalf +
              " " +
              t("half_cover").toLowerCase()}
          </Alert>
        )}

        <SlimCoupon
          round={round}
          onSubmit={onSubmit}
          theme={window.theme}
          showProPick={showProPick}
          setShowProPick={setShowProPick}
          showConfirm={showConfirm}
          setShowConfirm={setShowConfirm}
          submitting={submitting}
          submitFailed={submitFailed}
          onErrorMsgClose={() => handleErrorMessageClose()}
          submitError={submitError}
          stakeMultiplier={stakeMultiplier}
          setStakeMultiplier={setStakeMultiplier}
          onChangeFixture={onChangeFixture}
          showRoundInfo={showRoundInfo}
          setShowRoundInfo={setShowRoundInfo}
          showRSystem={showRSystem}
          setShowRSystem={setShowRSystem}
          proPickRows={proPickRows}
          setProPickRows={setProPickRows}
          onClear={() => {}}
          setChosenRSystem={setChosenRSystem}
          chosenRSystem={chosenRSystem}
          coupon={undefined}
          couponFixtureSummaries={undefined}
          freebet={undefined}
        />

        {isAfterDeadline && (round as any).operatorPrizePlan && (
          <WinInfoRound round={round as any} isRightToLeft={isRightToLeft} />
        )}
      </>
    </>
  );
}

export default CouponForm;
